import styled from "styled-components";

import { CheckCircle, ErrorBox, Warning } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useGoogleAdsRemoveKeywords } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-remove-keywords";
import { useGoogleAdsWriteKeywords } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-write-keywords";
import type {
    GoogleAdsKeywordMutateStatus,
    IGoogleAdsKeywordMutateResult,
} from "app/pages/digital-marketing/shared/google-ads/services/google-ads-proxy-client";

import type { GoogleAdsActionType, PendingGoogleAdsAction } from "./google-ads-actions.types";

export interface IGoogleAdsActionModalOutcome {
    // `succeeded` reflects whether the mutation itself completed (hook-level `isSuccess`) - not
    // whether every individual keyword succeeded, since a mixed add/duplicate result is still a
    // completed run per SIM-114. Only a completed run clears the caller's row selection.
    succeeded: boolean;
    // SIM-115: only set when `succeeded` is true - the raw per-keyword results the mutation
    // resolved with, threaded out so the page can append a session write-action log entry
    // without this modal needing to know anything about that log.
    results?: IGoogleAdsKeywordMutateResult[];
}

interface IGoogleAdsActionConfirmationModalProps {
    pendingAction: PendingGoogleAdsAction;
    onClose: (outcome: IGoogleAdsActionModalOutcome) => void;
}

interface IActionCopy {
    summary: (keywordCount: number) => string;
    confirmLabel: string;
}

const ACTION_COPY: Record<GoogleAdsActionType, IActionCopy> = {
    add: {
        summary: (keywordCount) => `Add ${keywordCount} keyword${keywordCount === 1 ? "" : "s"} to Google Ads`,
        confirmLabel: "Add to Google Ads",
    },
    negative: {
        summary: (keywordCount) => `Add ${keywordCount} keyword${keywordCount === 1 ? "" : "s"} as negative keywords`,
        confirmLabel: "Add as negative keywords",
    },
    remove: {
        summary: (keywordCount) => `Remove ${keywordCount} keyword${keywordCount === 1 ? "" : "s"} from Google Ads`,
        confirmLabel: "Remove from Google Ads",
    },
};

// The per-status result labels are phrased for the create actions ("Added"); "remove" overrides
// the success label to "Removed" below so the result list reads correctly for either flow.
const RESULT_COPY: Record<
    GoogleAdsKeywordMutateStatus,
    { label: string; icon: typeof CheckCircle; iconColor: string }
> = {
    success: { label: "Added", icon: CheckCircle, iconColor: colorsPalettes.green[300] },
    duplicate: {
        label: "Already exists in Google Ads",
        icon: Warning,
        iconColor: colorsPalettes.yellow[500],
    },
    error: { label: "Failed", icon: ErrorBox, iconColor: colorsPalettes.red[300] },
};

const KeywordListWrapper = styled(Stack)`
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 4px;
`;

const KeywordRow = styled(Flex)`
    padding: 8px 12px;
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};

    &:last-child {
        border-bottom: none;
    }
`;

const buildAccountLabel = (customerId: string, loginCustomerId?: string): string =>
    loginCustomerId ? `${customerId} (via manager account ${loginCustomerId})` : customerId;

export const GoogleAdsActionConfirmationModal = ({
    pendingAction,
    onClose,
}: IGoogleAdsActionConfirmationModalProps) => {
    const writeKeywords = useGoogleAdsWriteKeywords();
    const removeKeywords = useGoogleAdsRemoveKeywords();
    const { type, keywords, linkage } = pendingAction;
    const copy = ACTION_COPY[type];
    const isRemove = type === "remove";

    // Both hooks resolve to the same per-keyword result shape, so the render below can treat them
    // interchangeably - only the confirm call differs.
    const activeMutation = isRemove ? removeKeywords : writeKeywords;

    const handleConfirm = (): void => {
        if (isRemove) {
            removeKeywords.mutate({
                customerId: linkage.customerId,
                loginCustomerId: linkage.loginCustomerId,
                adGroupId: linkage.adGroupId,
                keywords,
            });
            return;
        }
        writeKeywords.mutate({
            customerId: linkage.customerId,
            loginCustomerId: linkage.loginCustomerId,
            adGroupResourceName: linkage.adGroupResourceName,
            keywords,
            negative: type === "negative",
        });
    };

    const handleCancel = (): void => onClose({ succeeded: false });
    const handleDone = (): void => onClose({ succeeded: true, results: activeMutation.data });

    const handleCloseButtonClick = (): void => {
        if (activeMutation.isPending) {
            return;
        }
        if (activeMutation.isSuccess) {
            handleDone();
        } else {
            handleCancel();
        }
    };

    const headerTitle = activeMutation.isError
        ? "Couldn't complete this action"
        : activeMutation.isSuccess
          ? "Google Ads result"
          : "Confirm Google Ads action";

    return (
        <Modal
            isOpen
            data-automation="google-ads-action-confirmation-modal"
            customStyles={{ content: { width: "520px" } }}
        >
            <Modal.Header>
                <Text
                    fontVariant="h5_2"
                    color={colorsPalettes.carbon["500"]}
                >
                    {headerTitle}
                </Text>
                <Modal.CloseButton onClick={handleCloseButtonClick} />
            </Modal.Header>
            <Modal.Content gap="16px">
                {activeMutation.isError ? (
                    <Flex
                        alignItems="center"
                        gap="8px"
                        data-automation="google-ads-action-confirmation-error"
                    >
                        <ErrorBox
                            color={colorsPalettes.red[300]}
                            size="sm"
                        />
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {activeMutation.error instanceof Error
                                ? activeMutation.error.message
                                : "Something went wrong while writing to Google Ads. Please try again."}
                        </Text>
                    </Flex>
                ) : activeMutation.isSuccess ? (
                    <KeywordListWrapper
                        gap="0"
                        data-automation="google-ads-action-confirmation-results"
                    >
                        {(activeMutation.data ?? []).map((result) => {
                            const resultCopy = RESULT_COPY[result.status];
                            const ResultIcon = resultCopy.icon;
                            const successLabel = isRemove ? "Removed" : resultCopy.label;
                            return (
                                <KeywordRow
                                    key={result.keyword}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="12px"
                                >
                                    <Text
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {result.keyword}
                                    </Text>
                                    <Flex
                                        alignItems="center"
                                        gap="6px"
                                    >
                                        <ResultIcon
                                            color={resultCopy.iconColor}
                                            size="xs"
                                        />
                                        <Text
                                            fontVariant="caption"
                                            color={colorsPalettes.carbon[400]}
                                        >
                                            {result.status === "error" && result.message
                                                ? result.message
                                                : result.status === "success"
                                                  ? successLabel
                                                  : resultCopy.label}
                                        </Text>
                                    </Flex>
                                </KeywordRow>
                            );
                        })}
                    </KeywordListWrapper>
                ) : (
                    <Stack
                        gap="16px"
                        data-automation="google-ads-action-confirmation-summary"
                    >
                        <Text
                            fontVariant="subtitle1"
                            color={colorsPalettes.carbon[500]}
                        >
                            {copy.summary(keywords.length)}
                        </Text>
                        <Stack gap="4px">
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                Account: {buildAccountLabel(linkage.customerId, linkage.loginCustomerId)}
                            </Text>
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                Campaign: {linkage.campaignName}
                            </Text>
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                Ad group: {linkage.adGroupName}
                            </Text>
                        </Stack>
                        <KeywordListWrapper gap="0">
                            {keywords.map((keyword) => (
                                <KeywordRow key={keyword}>
                                    <Text
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {keyword}
                                    </Text>
                                </KeywordRow>
                            ))}
                        </KeywordListWrapper>
                    </Stack>
                )}
            </Modal.Content>
            <Modal.Footer>
                {activeMutation.isError ? (
                    <>
                        <Button
                            type="flat"
                            onClick={handleCancel}
                            data-automation="google-ads-action-confirmation-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            data-automation="google-ads-action-confirmation-retry"
                        >
                            Retry
                        </Button>
                    </>
                ) : activeMutation.isSuccess ? (
                    <Button
                        onClick={handleDone}
                        data-automation="google-ads-action-confirmation-done"
                    >
                        Done
                    </Button>
                ) : (
                    <>
                        <Button
                            type="flat"
                            onClick={handleCancel}
                            isDisabled={activeMutation.isPending}
                            data-automation="google-ads-action-confirmation-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            isLoading={activeMutation.isPending}
                            isDisabled={activeMutation.isPending}
                            data-automation="google-ads-action-confirmation-confirm"
                        >
                            {copy.confirmLabel}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};
